/**
 * MapBackdrop — full-viewport "locations dashboard" backdrop.
 *
 * Recreates the dark-navy dotted-world-map look (vignette + halftone
 * continents + neon ring markers + ghost numerals) entirely in CSS/SVG —
 * no image assets, no dependencies. Rendered fixed behind the app content;
 * cards/panels stay solid on top so text remains readable.
 */
import { useMemo } from 'react';

/* ─── continent silhouettes as ellipses in a 1000×460 map space ───────────
 * Coarse on purpose: a dot grid is masked to these regions, giving the
 * recognisable world-map halftone without shipping a geo asset. */
type Ellipse = { cx: number; cy: number; rx: number; ry: number };
const CONTINENTS: Ellipse[] = [
  { cx: 230, cy: 150, rx: 120, ry: 95 },   // North America
  { cx: 120, cy: 115, rx: 45,  ry: 28 },   // Alaska / NW
  { cx: 355, cy: 80,  rx: 38,  ry: 48 },   // Greenland
  { cx: 300, cy: 250, rx: 26,  ry: 46 },   // Central America
  { cx: 330, cy: 350, rx: 75,  ry: 105 },  // South America (upper)
  { cx: 305, cy: 420, rx: 26,  ry: 46 },   // South America (cone)
  { cx: 495, cy: 120, rx: 12,  ry: 18 },   // UK / Ireland
  { cx: 530, cy: 135, rx: 55,  ry: 50 },   // Europe
  { cx: 560, cy: 300, rx: 95,  ry: 120 },  // Africa
  { cx: 645, cy: 360, rx: 12,  ry: 28 },   // Madagascar
  { cx: 600, cy: 215, rx: 50,  ry: 45 },   // Middle East
  { cx: 740, cy: 145, rx: 185, ry: 105 },  // Asia (main)
  { cx: 690, cy: 240, rx: 45,  ry: 50 },   // India
  { cx: 800, cy: 285, rx: 55,  ry: 35 },   // SE Asia
  { cx: 820, cy: 312, rx: 70,  ry: 22 },   // Indonesia
  { cx: 905, cy: 165, rx: 14,  ry: 35 },   // Japan
  { cx: 870, cy: 380, rx: 75,  ry: 48 },   // Australia
  { cx: 965, cy: 422, rx: 12,  ry: 22 },   // New Zealand
];

const inLand = (x: number, y: number) =>
  CONTINENTS.some(e => ((x - e.cx) / e.rx) ** 2 + ((y - e.cy) / e.ry) ** 2 <= 1);

// deterministic 0..1 hash so dot opacity jitter is stable across renders
const hash = (i: number) => {
  const s = Math.sin(i * 12.9898) * 43758.5453;
  return s - Math.floor(s);
};

type Dot = { x: number; y: number; o: number };

/** calm location nodes — brand gold (primary) + cyan, understated */
const MARKERS = [
  { x: 175, y: 178, color: '#cda42e', r: 4, ring: true  },   // California (brand gold, focal)
  { x: 770, y: 150, color: '#06b6d4', r: 3, ring: false },   // Asia (cyan)
];

const NUMERALS = [
  { t: '01', x: 430, y: 110, size: 96 },
  { t: '05', x: 110, y: 250, size: 128 },
  { t: '03', x: 885, y: 235, size: 110 },
];

export default function MapBackdrop() {
  const dots = useMemo<Dot[]>(() => {
    const out: Dot[] = [];
    const step = 10;
    let i = 0;
    for (let row = 0; row * step <= 460; row++) {
      const y = row * step;
      const offset = row % 2 ? step / 2 : 0;          // hex-stagger for organic feel
      for (let x = offset; x <= 1000; x += step) {
        i++;
        if (inLand(x, y)) out.push({ x, y, o: 0.05 + hash(i) * 0.11 });
      }
    }
    return out;
  }, []);

  return (
    <div
      aria-hidden
      className="fixed inset-0 -z-10 pointer-events-none overflow-hidden"
      style={{
        background: [
          'radial-gradient(55% 45% at 50% 0%, rgba(40,70,120,0.12), transparent 70%)',
          'radial-gradient(120% 120% at 50% 26%, #0b1226 0%, #080d1a 50%, #04070e 100%)',
        ].join(','),
      }}
    >
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1000 460"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <filter id="mb-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="3.2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ghost numerals */}
        {NUMERALS.map(n => (
          <text
            key={n.t}
            x={n.x}
            y={n.y}
            fontSize={n.size}
            fontWeight={800}
            fill="rgba(226,236,255,0.028)"
            fontFamily="system-ui, sans-serif"
            letterSpacing="-4"
          >
            {n.t}
          </text>
        ))}

        {/* dotted continents */}
        {dots.map((d, i) => (
          <circle key={i} cx={d.x} cy={d.y} r={1.3} fill={`rgba(124,148,196,${d.o})`} />
        ))}

        {/* calm location nodes (brand gold + cyan) */}
        {MARKERS.map((m, i) => (
          <g key={i} filter="url(#mb-glow)">
            {m.ring && (
              <circle cx={m.x} cy={m.y} r={m.r + 5} fill="none" stroke={m.color} strokeWidth="1" opacity="0.35" />
            )}
            <circle cx={m.x} cy={m.y} r={m.r} fill="none" stroke={m.color} strokeWidth="1.4" opacity="0.7" />
            <circle cx={m.x} cy={m.y} r={1.6} fill={m.color} opacity="0.8" />
          </g>
        ))}
      </svg>

      {/* vignette to sink the edges into black like the mockup */}
      <div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(130% 95% at 50% 42%, transparent 38%, rgba(1,2,6,0.8) 100%)' }}
      />
    </div>
  );
}
